import React, { useState, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { normalizeMediaObject, getImageByWidth } from 'api';
import { useGlobalContext } from 'globalContext';
import { useAnalytics } from 'hooks';
import { Route, Switch, Redirect } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import { Playlist, useWrapperContext, menuItemTrigger } from 'msp-components';
import Error404 from 'components/Error404';

const normalizePlaylistMediaItem = (item, imgWidth = 1500, thumbnailWidth = 200) => ({
  ...normalizeMediaObject(item.media, imgWidth, thumbnailWidth),
  id: item.uid,
  type: item.type,
  slug: item.slug,
  groupId: item.group,
  posterFrameDuration: item.posterDuration,
});

const normalizePlaylistData = (items, imgWidth = 1500, thumbnailWidth = 200, includeThumbnails = true) => items.reduce((result, item) => {
  switch (item.type) {
    case 'label':
      result.push({
        id: item.uid,
        type: item.type,
        title: item.name,
      });
      break;
    case 'media':
      if (includeThumbnails) {
        result.push(normalizePlaylistMediaItem(item, imgWidth, thumbnailWidth));
      } else {
        const { thumbnail, ...rest } = normalizePlaylistMediaItem(item, imgWidth, thumbnailWidth);
        result.push(rest);
      }
      break;
    case 'group': {
      // omit empty groups
      if (!item.items.length > 0) break;
      let thumbnail;
      if (includeThumbnails) {
        thumbnail = (getImageByWidth(get(item, 'groupMedia.previewImages.horizontalThumbnail', []), 200) || {}).url;
      }
      result.push({
        id: item.uid,
        type: item.type,
        title: item.name,
        description: item.description,
        thumbnail,
      });
      item.items.forEach(item => {
        if (includeThumbnails) {
          result.push(normalizePlaylistMediaItem(item, imgWidth, thumbnailWidth));
        } else {
          const { thumbnail, ...rest } = normalizePlaylistMediaItem(item, imgWidth, thumbnailWidth);
          result.push(rest);
        }
      });
      break;
    }
    default:
      break;
  }
  return result;
}, []);

const PlaylistTitle = ({ routeProps, uid, items, displayOptions, onPageTitleChange, onSubItemChange }) => {
  const [containerWidth, setContainerWidth] = useState();
  const handleResize = width => setContainerWidth(width);

  const { toggleMenu } = useWrapperContext();

  const { match, history } = routeProps;

  const { playlistDefaultState, showThumbnails, onEnd } = displayOptions;

  const playlistItems = useMemo(() => {
    const imgWidth = containerWidth && containerWidth * 1.5;
    const thumbs = showThumbnails !== false;
    return normalizePlaylistData(items, imgWidth, 200, thumbs);
  }, [items, containerWidth, showThumbnails]);
  const mediaItems = useMemo(() => playlistItems.filter(({ type }) => type === 'media'), [playlistItems]);
  const ids = mediaItems.map(({ id }) => id);
  const slugsById = useMemo(() => (
    mediaItems.reduce((result, { id, slug }) => {
      // Ensure no duplicate slugs
      result[id] = Object.values(result).includes(slug) ? `${slug}-${id.slice(0, 8)}` : slug;
      return result;
    }, {})
  ), [mediaItems]);
  const idsBySlug = Object.entries(slugsById).reduce((result, [id, slug]) => {
    result[slug] = id;
    return result;
  }, {});

  // Organize raw media objects by playlist item id
  const mediaByItemId = useMemo(() => items.reduce((result, item) => {
    if (item.type === 'media') {
      result[item.uid] = item.media;
    } else if (item.type === 'group') {
      item.items.forEach(groupItem => result[groupItem.uid] = groupItem.media);
    }
    return result;
  }, {}), [items]);

  const [activePlaylistItemId, setActivePlaylistItemId] = useState(null);
  useEffect(() => {
    if (activePlaylistItemId) {
      const activeItem = playlistItems.find(({ id }) => id === activePlaylistItemId);
      onPageTitleChange(activeItem.title);
      onSubItemChange(mediaByItemId[activePlaylistItemId]);
    } else {
      onPageTitleChange(null);
      onSubItemChange(null);
    }
    return () => onSubItemChange(null);
  }, [activePlaylistItemId, mediaByItemId, playlistItems, onPageTitleChange, onSubItemChange]);

  const [autoplayEnabled, setAutoplayEnabled] = useState(true);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const handleItemSelect = id => {
    setShouldAutoplay(true);
    history.push(`${match.url}/${slugsById[id]}`);
  };
  const handleMediaReady = () => {
    // Reset to stored value
    setShouldAutoplay(autoplayEnabled);
  };

  // pause player when menu item is opened
  const playerRef = useRef();
  const { activeMenuItem, toggleShareSheet, titlePaths, itemsOrdered } = useGlobalContext();
  useEffect(() => {
    if (activeMenuItem && playerRef.current) {
      playerRef.current.handleRequestPause();
    }
  }, [activeMenuItem]);

  // analytics event tracking
  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  const menuItemTriggerTimeoutRef = useRef();
  const handleMediaEnd = itemId => {
    const nextIndex = ids.findIndex(id => id === itemId) + 1;

    if (autoplayEnabled && nextIndex < ids.length) {
      const nextSlug = slugsById[ids[nextIndex]];
      history.push(`${match.url}/${nextSlug}`);
    }

    if (nextIndex === ids.length) {
      // We are already on the last item
      if (onEnd === 'share') {
        toggleShareSheet();
      } else if (onEnd.indexOf('menu__') === 0) {
        const menuItemId = onEnd.replace('menu__', '');
        toggleMenu(true);
        menuItemTriggerTimeoutRef.current = setTimeout(() => menuItemTrigger(menuItemId), 500);
      } else if (onEnd === 'next') {
        const nextItemId = itemsOrdered[itemsOrdered.indexOf(uid) + 1];
        const nextItemPath = nextItemId && titlePaths[nextItemId];
        if (nextItemPath) history.push(nextItemPath);
      } else {
        // if onEnd === 'poster' or is undefined
        playerRef.current.handleRequestSeek(0);
      }
    }
  };

  useEffect(() => {
    return () => clearTimeout(menuItemTriggerTimeoutRef.current);
  }, []);

  return (
    <Switch>
      <Route exact path={match.url} render={props => <Redirect to={`${match.url}/${slugsById[ids[0]]}`} />} />
      <Route
        exact
        path={`${match.url}/:itemSlug`}
        render={props => {
          // eslint-disable-next-line
          const { itemSlug } = props.match.params;
          if (!idsBySlug[itemSlug]) {
            // Try treating the param as an index instead.
            const index = parseInt(itemSlug, 10);
            if (isNaN(index) || index < 1 || index > ids.length) return <Error404 />;
            const toSlug = slugsById[ids[index - 1]];
            return <Redirect to={`${match.url}/${toSlug}`} />;
          }

          const activeItemId = idsBySlug[itemSlug];
          setActivePlaylistItemId(activeItemId);

          return (
            <>
              <ResizeDetector
                handleWidth
                onResize={handleResize}
                refreshMode="debounce"
                refreshRate={500}
              />
              <Playlist
                items={playlistItems}
                autoplayEnabled={autoplayEnabled}
                shouldAutoplay={shouldAutoplay}
                activeItemId={activeItemId}
                defaultPlaylistVisible={playlistDefaultState !== 'closed'}
                mediaPlayerRef={playerRef}
                onSelectItem={handleItemSelect}
                onToggleAutoplay={setAutoplayEnabled}
                onMediaReady={handleMediaReady}
                onMediaPlay={trackMediaPlay}
                onMediaProgress={trackMediaProgress}
                onMediaEnd={() => handleMediaEnd(activeItemId)}
              />
            </>
          );
        }}
      />
      <Route component={Error404} />
    </Switch>
  );
};

PlaylistTitle.propTypes = {
  routeProps: PropTypes.object,
  uid: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  displayOptions: PropTypes.shape({
    playlistDefaultState: PropTypes.oneOf(['open', 'closed']),
    showThumbnails: PropTypes.bool,
    onEnd: PropTypes.oneOf(['poster', 'share', 'next']),
  }),
  onPageTitleChange: PropTypes.func,
  onSubItemChange: PropTypes.func,
};

export default PlaylistTitle;
